import { CAMPAIGN_NAME_MAX_LENGTH, DEFAULT_SIGNATURE_TEMPLATE } from "@giftforward/naming";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import {
  DEFAULT_LANDING_HEADLINE,
  DEFAULT_LANDING_SUBTITLE,
  LANDING_COLOR_DEFAULTS,
} from "@/constants/landing";
import type {
  ExpiryMode,
  LandingSettings,
  ReminderEmailState,
  ScheduleMode,
  Stage3EmailState,
  UnboxingSettings,
} from "@/interfaces/CampaignBuilder";
import type { TokenEmail } from "@/interfaces/emailPreview";
import { RECIPIENT_ONLY_TAGS } from "@/utils/strandedTags";

/** The screen a View Only client sees on opening the builder URL directly. */
export const CAMPAIGN_BUILDER_BLOCKED_TEXT = {
  title: "Your role can't send gifts",
  body: "You have View Only access, so you can see campaigns and reports but not create or send campaigns. If you need to send gifts, ask your administrator to change your role.",
  action: "Back to dashboard",
} as const;

/** The builder opened on a campaign the caller may view but not edit (a group where they are View Only). */
export const CAMPAIGN_EDIT_BLOCKED_TEXT = {
  title: "You can't edit this campaign",
  body: "You have View Only access to this campaign's group, so you can see it in My Campaigns but not open it in the Campaign Builder. If you need to change it, ask your administrator.",
  action: "Back to My Campaigns",
} as const;

// ── Default email content ──────────────────────────────────────────────────
export const EMAIL_FIELD_LIMITS = {
  subject: 60,
  message: 1000,
  signature: 40,
} as const;

export const DEFAULT_EMAIL_SUBJECT = "@FirstName, you have a gift from @Signature";
export const DEFAULT_EMAIL_MESSAGE_HTML =
  "<p>Hi @FirstName,</p><p><br></p><p>@SenderFirstName at @SenderCompany has sent you a choice of gifts that give back. Each one supports a nonprofit or small social enterprise. Select your gift today and help us do some good in the world!</p>";
// The default Step-3 email signature IS the shared @Signature fallback — one source of truth, so
// the email the author sees pre-filled matches what a blank per-recipient signature resolves to.
export const DEFAULT_EMAIL_SIGNATURE_HTML = DEFAULT_SIGNATURE_TEMPLATE;

// ── Quick message templates ────────────────────────────────────────────────
export const QUICK_TEMPLATES = [
  {
    name: "Professional Thank You",
    desc: "A token of our appreciation",
    Icon: HandshakeOutlinedIcon,
    subject: "A gift for you — thank you for everything",
    messageHtml:
      "<p>Dear @FirstName,</p><p><br></p><p>We wanted to take a moment to express our sincere gratitude for your partnership. Please enjoy this gift as a small token of our appreciation.</p>",
  },
  {
    name: "Holiday Celebration",
    desc: "Happy Holidays from our team!",
    Icon: CelebrationOutlinedIcon,
    subject: "Happy Holidays — a gift from our team to you!",
    messageHtml:
      "<p>Dear @FirstName,</p><p><br></p><p>Happy Holidays from our team! As the year comes to a close, we wanted to share some joy with a gift chosen just for you. Wishing you a wonderful holiday season.</p>",
  },
  {
    name: "Milestone Achievement",
    desc: "Celebrating your achievement!",
    Icon: EmojiEventsOutlinedIcon,
    subject: "Congratulations on your milestone!",
    messageHtml:
      "<p>Dear @FirstName,</p><p><br></p><p>Congratulations on reaching this incredible milestone! Your dedication and hard work have not gone unnoticed. Please enjoy this gift as a celebration of your achievement.</p>",
  },
] as const;

// The anchor phrase ("send date" vs "activation date") is swapped at render time in the Expiration
// accordion + the Step-4 summary for manual campaigns; these are the gfg-mode defaults.
export const EXPIRY_LABELS: Record<ExpiryMode, string> = {
  "30": "30 days from send date",
  "60": "60 days from send date",
  "90": "90 days from send date",
  "6mo": "6 months from send date",
  custom: "Custom date",
};

/** Step 2's Schedule options, by name: the radios and the GFG-443 "file has send dates" prompt. */
export const SCHEDULE_MODE_LABELS: Record<ScheduleMode, string> = {
  asap: "Send ASAP",
  certain: "Schedule for later",
  different: "Send on different dates",
};

// GFG-438: 60 days is the default and recommended preset for all campaign types.
export const DEFAULT_EXPIRY_MODE: ExpiryMode = "60";

/**
 * The wall-clock time a custom expiry date closes at, in the campaign's Schedule zone. Must match
 * gfgf-api's `CUSTOM_EXPIRY_TIME` (constants/date_time.ts); timezone-helper.test.ts asserts they agree.
 */
export const CUSTOM_EXPIRY_TIME = "19:00";

/**
 * Wall-clock time a recipient's send date with no time goes out at, in the campaign's Schedule zone
 * (GFG-443: one rule everywhere, the campaign's zone). Must match gfgf-api's `DEFAULT_SEND_TIME`
 * (constants/date_time.ts); timezone-helper.test.ts asserts they agree.
 */
export const DEFAULT_SEND_TIME = "09:00";

export const CAMPAIGN_BUILDER_STEPS = [
  { number: 1, label: "Collection", path: "/campaign/create/collection" },
  { number: 2, label: "Recipients", path: "/campaign/create/recipients" },
  { number: 3, label: "Design", path: "/campaign/create/design" },
  { number: 4, label: "Review", path: "/campaign/create/review" },
] as const;

export type CampaignBuilderStepNumber = (typeof CAMPAIGN_BUILDER_STEPS)[number]["number"];

export const FIRST_STEP: CampaignBuilderStepNumber = 1;
export const LAST_STEP: CampaignBuilderStepNumber = 4;

// US first, then alphabetical — matches prototype order
export const AVAILABLE_COUNTRIES: { code: string; flag: string; name: string }[] = [
  { code: "US", flag: "🇺🇸", name: "USA" },
  { code: "AF", flag: "🇦🇫", name: "Afghanistan" },
  { code: "AX", flag: "🇦🇽", name: "Aland Islands" },
  { code: "AL", flag: "🇦🇱", name: "Albania" },
  { code: "DZ", flag: "🇩🇿", name: "Algeria" },
  { code: "AD", flag: "🇦🇩", name: "Andorra" },
  { code: "AO", flag: "🇦🇴", name: "Angola" },
  { code: "AI", flag: "🇦🇮", name: "Anguilla" },
  { code: "AG", flag: "🇦🇬", name: "Antigua And Barbuda" },
  { code: "AR", flag: "🇦🇷", name: "Argentina" },
  { code: "AM", flag: "🇦🇲", name: "Armenia" },
  { code: "AW", flag: "🇦🇼", name: "Aruba" },
  { code: "AC", flag: "🇦🇨", name: "Ascension Island" },
  { code: "AU", flag: "🇦🇺", name: "Australia" },
  { code: "AT", flag: "🇦🇹", name: "Austria" },
  { code: "AZ", flag: "🇦🇿", name: "Azerbaijan" },
  { code: "BS", flag: "🇧🇸", name: "Bahamas" },
  { code: "BH", flag: "🇧🇭", name: "Bahrain" },
  { code: "BD", flag: "🇧🇩", name: "Bangladesh" },
  { code: "BB", flag: "🇧🇧", name: "Barbados" },
  { code: "BY", flag: "🇧🇾", name: "Belarus" },
  { code: "BE", flag: "🇧🇪", name: "Belgium" },
  { code: "BZ", flag: "🇧🇿", name: "Belize" },
  { code: "BJ", flag: "🇧🇯", name: "Benin" },
  { code: "BM", flag: "🇧🇲", name: "Bermuda" },
  { code: "BT", flag: "🇧🇹", name: "Bhutan" },
  { code: "BO", flag: "🇧🇴", name: "Bolivia" },
  { code: "BA", flag: "🇧🇦", name: "Bosnia And Herzegovina" },
  { code: "BW", flag: "🇧🇼", name: "Botswana" },
  { code: "BV", flag: "🇧🇻", name: "Bouvet Island" },
  { code: "BR", flag: "🇧🇷", name: "Brazil" },
  { code: "IO", flag: "🇮🇴", name: "British Indian Ocean Territory" },
  { code: "BN", flag: "🇧🇳", name: "Brunei" },
  { code: "BG", flag: "🇧🇬", name: "Bulgaria" },
  { code: "BF", flag: "🇧🇫", name: "Burkina Faso" },
  { code: "BI", flag: "🇧🇮", name: "Burundi" },
  { code: "KH", flag: "🇰🇭", name: "Cambodia" },
  { code: "CA", flag: "🇨🇦", name: "Canada" },
  { code: "CV", flag: "🇨🇻", name: "Cape Verde" },
  { code: "BQ", flag: "🇧🇶", name: "Caribbean Netherlands" },
  { code: "KY", flag: "🇰🇾", name: "Cayman Islands" },
  { code: "CF", flag: "🇨🇫", name: "Central African Republic" },
  { code: "TD", flag: "🇹🇩", name: "Chad" },
  { code: "CL", flag: "🇨🇱", name: "Chile" },
  { code: "CN", flag: "🇨🇳", name: "China" },
  { code: "CX", flag: "🇨🇽", name: "Christmas Island" },
  { code: "CC", flag: "🇨🇨", name: "Cocos (Keeling) Islands" },
  { code: "CO", flag: "🇨🇴", name: "Colombia" },
  { code: "KM", flag: "🇰🇲", name: "Comoros" },
  { code: "CG", flag: "🇨🇬", name: "Congo" },
  { code: "CD", flag: "🇨🇩", name: "Congo, The Democratic Republic Of The" },
  { code: "CK", flag: "🇨🇰", name: "Cook Islands" },
  { code: "CR", flag: "🇨🇷", name: "Costa Rica" },
  { code: "CI", flag: "🇨🇮", name: "Côte d'Ivoire" },
  { code: "HR", flag: "🇭🇷", name: "Croatia" },
  { code: "CU", flag: "🇨🇺", name: "Cuba" },
  { code: "CW", flag: "🇨🇼", name: "Curaçao" },
  { code: "CY", flag: "🇨🇾", name: "Cyprus" },
  { code: "CZ", flag: "🇨🇿", name: "Czechia" },
  { code: "DK", flag: "🇩🇰", name: "Denmark" },
  { code: "DJ", flag: "🇩🇯", name: "Djibouti" },
  { code: "DM", flag: "🇩🇲", name: "Dominica" },
  { code: "DO", flag: "🇩🇴", name: "Dominican Republic" },
  { code: "EC", flag: "🇪🇨", name: "Ecuador" },
  { code: "EG", flag: "🇪🇬", name: "Egypt" },
  { code: "SV", flag: "🇸🇻", name: "El Salvador" },
  { code: "GQ", flag: "🇬🇶", name: "Equatorial Guinea" },
  { code: "ER", flag: "🇪🇷", name: "Eritrea" },
  { code: "EE", flag: "🇪🇪", name: "Estonia" },
  { code: "SZ", flag: "🇸🇿", name: "Eswatini" },
  { code: "ET", flag: "🇪🇹", name: "Ethiopia" },
  { code: "FK", flag: "🇫🇰", name: "Falkland Islands (Malvinas)" },
  { code: "FO", flag: "🇫🇴", name: "Faroe Islands" },
  { code: "FJ", flag: "🇫🇯", name: "Fiji" },
  { code: "FI", flag: "🇫🇮", name: "Finland" },
  { code: "FR", flag: "🇫🇷", name: "France" },
  { code: "GF", flag: "🇬🇫", name: "French Guiana" },
  { code: "PF", flag: "🇵🇫", name: "French Polynesia" },
  { code: "TF", flag: "🇹🇫", name: "French Southern Territories" },
  { code: "GA", flag: "🇬🇦", name: "Gabon" },
  { code: "GM", flag: "🇬🇲", name: "Gambia" },
  { code: "GE", flag: "🇬🇪", name: "Georgia" },
  { code: "DE", flag: "🇩🇪", name: "Germany" },
  { code: "GH", flag: "🇬🇭", name: "Ghana" },
  { code: "GI", flag: "🇬🇮", name: "Gibraltar" },
  { code: "GR", flag: "🇬🇷", name: "Greece" },
  { code: "GL", flag: "🇬🇱", name: "Greenland" },
  { code: "GD", flag: "🇬🇩", name: "Grenada" },
  { code: "GP", flag: "🇬🇵", name: "Guadeloupe" },
  { code: "GT", flag: "🇬🇹", name: "Guatemala" },
  { code: "GG", flag: "🇬🇬", name: "Guernsey" },
  { code: "GN", flag: "🇬🇳", name: "Guinea" },
  { code: "GW", flag: "🇬🇼", name: "Guinea Bissau" },
  { code: "GY", flag: "🇬🇾", name: "Guyana" },
  { code: "HT", flag: "🇭🇹", name: "Haiti" },
  { code: "HM", flag: "🇭🇲", name: "Heard Island And Mcdonald Islands" },
  { code: "VA", flag: "🇻🇦", name: "Holy See (Vatican City State)" },
  { code: "HN", flag: "🇭🇳", name: "Honduras" },
  { code: "HK", flag: "🇭🇰", name: "Hong Kong" },
  { code: "HU", flag: "🇭🇺", name: "Hungary" },
  { code: "IS", flag: "🇮🇸", name: "Iceland" },
  { code: "IN", flag: "🇮🇳", name: "India" },
  { code: "ID", flag: "🇮🇩", name: "Indonesia" },
  { code: "IR", flag: "🇮🇷", name: "Iran, Islamic Republic Of" },
  { code: "IQ", flag: "🇮🇶", name: "Iraq" },
  { code: "IE", flag: "🇮🇪", name: "Ireland" },
  { code: "IM", flag: "🇮🇲", name: "Isle Of Man" },
  { code: "IL", flag: "🇮🇱", name: "Israel" },
  { code: "IT", flag: "🇮🇹", name: "Italy" },
  { code: "JM", flag: "🇯🇲", name: "Jamaica" },
  { code: "JP", flag: "🇯🇵", name: "Japan" },
  { code: "JE", flag: "🇯🇪", name: "Jersey" },
  { code: "JO", flag: "🇯🇴", name: "Jordan" },
  { code: "KZ", flag: "🇰🇿", name: "Kazakhstan" },
  { code: "KE", flag: "🇰🇪", name: "Kenya" },
  { code: "KI", flag: "🇰🇮", name: "Kiribati" },
  { code: "KP", flag: "🇰🇵", name: "Korea, Democratic People's Republic Of" },
  { code: "XK", flag: "🇽🇰", name: "Kosovo" },
  { code: "KW", flag: "🇰🇼", name: "Kuwait" },
  { code: "KG", flag: "🇰🇬", name: "Kyrgyzstan" },
  { code: "LA", flag: "🇱🇦", name: "Lao People's Democratic Republic" },
  { code: "LV", flag: "🇱🇻", name: "Latvia" },
  { code: "LB", flag: "🇱🇧", name: "Lebanon" },
  { code: "LS", flag: "🇱🇸", name: "Lesotho" },
  { code: "LR", flag: "🇱🇷", name: "Liberia" },
  { code: "LY", flag: "🇱🇾", name: "Libyan Arab Jamahiriya" },
  { code: "LI", flag: "🇱🇮", name: "Liechtenstein" },
  { code: "LT", flag: "🇱🇹", name: "Lithuania" },
  { code: "LU", flag: "🇱🇺", name: "Luxembourg" },
  { code: "MO", flag: "🇲🇴", name: "Macao" },
  { code: "MG", flag: "🇲🇬", name: "Madagascar" },
  { code: "MW", flag: "🇲🇼", name: "Malawi" },
  { code: "MY", flag: "🇲🇾", name: "Malaysia" },
  { code: "MV", flag: "🇲🇻", name: "Maldives" },
  { code: "ML", flag: "🇲🇱", name: "Mali" },
  { code: "MT", flag: "🇲🇹", name: "Malta" },
  { code: "MQ", flag: "🇲🇶", name: "Martinique" },
  { code: "MR", flag: "🇲🇷", name: "Mauritania" },
  { code: "MU", flag: "🇲🇺", name: "Mauritius" },
  { code: "YT", flag: "🇾🇹", name: "Mayotte" },
  { code: "MX", flag: "🇲🇽", name: "Mexico" },
  { code: "MD", flag: "🇲🇩", name: "Moldova, Republic of" },
  { code: "MC", flag: "🇲🇨", name: "Monaco" },
  { code: "MN", flag: "🇲🇳", name: "Mongolia" },
  { code: "ME", flag: "🇲🇪", name: "Montenegro" },
  { code: "MS", flag: "🇲🇸", name: "Montserrat" },
  { code: "MA", flag: "🇲🇦", name: "Morocco" },
  { code: "MZ", flag: "🇲🇿", name: "Mozambique" },
  { code: "MM", flag: "🇲🇲", name: "Myanmar" },
  { code: "NA", flag: "🇳🇦", name: "Namibia" },
  { code: "NR", flag: "🇳🇷", name: "Nauru" },
  { code: "NP", flag: "🇳🇵", name: "Nepal" },
  { code: "NL", flag: "🇳🇱", name: "Netherlands" },
  { code: "AN", flag: "🇦🇳", name: "Netherlands Antilles" },
  { code: "NC", flag: "🇳🇨", name: "New Caledonia" },
  { code: "NZ", flag: "🇳🇿", name: "New Zealand" },
  { code: "NI", flag: "🇳🇮", name: "Nicaragua" },
  { code: "NE", flag: "🇳🇪", name: "Niger" },
  { code: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "NU", flag: "🇳🇺", name: "Niue" },
  { code: "NF", flag: "🇳🇫", name: "Norfolk Island" },
  { code: "MK", flag: "🇲🇰", name: "North Macedonia" },
  { code: "NO", flag: "🇳🇴", name: "Norway" },
  { code: "OM", flag: "🇴🇲", name: "Oman" },
  { code: "PK", flag: "🇵🇰", name: "Pakistan" },
  { code: "PS", flag: "🇵🇸", name: "Palestinian Territory, Occupied" },
  { code: "PA", flag: "🇵🇦", name: "Panama" },
  { code: "PG", flag: "🇵🇬", name: "Papua New Guinea" },
  { code: "PY", flag: "🇵🇾", name: "Paraguay" },
  { code: "PE", flag: "🇵🇪", name: "Peru" },
  { code: "PH", flag: "🇵🇭", name: "Philippines" },
  { code: "PN", flag: "🇵🇳", name: "Pitcairn" },
  { code: "PL", flag: "🇵🇱", name: "Poland" },
  { code: "PT", flag: "🇵🇹", name: "Portugal" },
  { code: "QA", flag: "🇶🇦", name: "Qatar" },
  { code: "CM", flag: "🇨🇲", name: "Republic of Cameroon" },
  { code: "RE", flag: "🇷🇪", name: "Reunion" },
  { code: "RO", flag: "🇷🇴", name: "Romania" },
  { code: "RU", flag: "🇷🇺", name: "Russia" },
  { code: "RW", flag: "🇷🇼", name: "Rwanda" },
  { code: "BL", flag: "🇧🇱", name: "Saint Barthélemy" },
  { code: "SH", flag: "🇸🇭", name: "Saint Helena" },
  { code: "KN", flag: "🇰🇳", name: "Saint Kitts And Nevis" },
  { code: "LC", flag: "🇱🇨", name: "Saint Lucia" },
  { code: "MF", flag: "🇲🇫", name: "Saint Martin" },
  { code: "PM", flag: "🇵🇲", name: "Saint Pierre And Miquelon" },
  { code: "WS", flag: "🇼🇸", name: "Samoa" },
  { code: "SM", flag: "🇸🇲", name: "San Marino" },
  { code: "ST", flag: "🇸🇹", name: "Sao Tome And Principe" },
  { code: "SA", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "SN", flag: "🇸🇳", name: "Senegal" },
  { code: "RS", flag: "🇷🇸", name: "Serbia" },
  { code: "SC", flag: "🇸🇨", name: "Seychelles" },
  { code: "SL", flag: "🇸🇱", name: "Sierra Leone" },
  { code: "SG", flag: "🇸🇬", name: "Singapore" },
  { code: "SX", flag: "🇸🇽", name: "Sint Maarten" },
  { code: "SK", flag: "🇸🇰", name: "Slovakia" },
  { code: "SI", flag: "🇸🇮", name: "Slovenia" },
  { code: "SB", flag: "🇸🇧", name: "Solomon Islands" },
  { code: "SO", flag: "🇸🇴", name: "Somalia" },
  { code: "ZA", flag: "🇿🇦", name: "South Africa" },
  { code: "GS", flag: "🇬🇸", name: "South Georgia And The South Sandwich Islands" },
  { code: "KR", flag: "🇰🇷", name: "South Korea" },
  { code: "SS", flag: "🇸🇸", name: "South Sudan" },
  { code: "ES", flag: "🇪🇸", name: "Spain" },
  { code: "LK", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "VC", flag: "🇻🇨", name: "St. Vincent" },
  { code: "SD", flag: "🇸🇩", name: "Sudan" },
  { code: "SR", flag: "🇸🇷", name: "Suriname" },
  { code: "SJ", flag: "🇸🇯", name: "Svalbard And Jan Mayen" },
  { code: "SE", flag: "🇸🇪", name: "Sweden" },
  { code: "CH", flag: "🇨🇭", name: "Switzerland" },
  { code: "SY", flag: "🇸🇾", name: "Syria" },
  { code: "TW", flag: "🇹🇼", name: "Taiwan" },
  { code: "TJ", flag: "🇹🇯", name: "Tajikistan" },
  { code: "TZ", flag: "🇹🇿", name: "Tanzania, United Republic Of" },
  { code: "TH", flag: "🇹🇭", name: "Thailand" },
  { code: "TL", flag: "🇹🇱", name: "Timor Leste" },
  { code: "TG", flag: "🇹🇬", name: "Togo" },
  { code: "TK", flag: "🇹🇰", name: "Tokelau" },
  { code: "TO", flag: "🇹🇴", name: "Tonga" },
  { code: "TT", flag: "🇹🇹", name: "Trinidad and Tobago" },
  { code: "TA", flag: "🇹🇦", name: "Tristan da Cunha" },
  { code: "TN", flag: "🇹🇳", name: "Tunisia" },
  { code: "TR", flag: "🇹🇷", name: "Turkey" },
  { code: "TM", flag: "🇹🇲", name: "Turkmenistan" },
  { code: "TC", flag: "🇹🇨", name: "Turks and Caicos Islands" },
  { code: "TV", flag: "🇹🇻", name: "Tuvalu" },
  { code: "UG", flag: "🇺🇬", name: "Uganda" },
  { code: "UA", flag: "🇺🇦", name: "Ukraine" },
  { code: "AE", flag: "🇦🇪", name: "United Arab Emirates" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "UM", flag: "🇺🇲", name: "United States Minor Outlying Islands" },
  { code: "UY", flag: "🇺🇾", name: "Uruguay" },
  { code: "UZ", flag: "🇺🇿", name: "Uzbekistan" },
  { code: "VU", flag: "🇻🇺", name: "Vanuatu" },
  { code: "VE", flag: "🇻🇪", name: "Venezuela" },
  { code: "VN", flag: "🇻🇳", name: "Vietnam" },
  { code: "VG", flag: "🇻🇬", name: "Virgin Islands, British" },
  { code: "WF", flag: "🇼🇫", name: "Wallis And Futuna" },
  { code: "EH", flag: "🇪🇭", name: "Western Sahara" },
  { code: "YE", flag: "🇾🇪", name: "Yemen" },
  { code: "ZM", flag: "🇿🇲", name: "Zambia" },
  { code: "ZW", flag: "🇿🇼", name: "Zimbabwe" },
];

export const BUDGET_PILLS = [5, 10, 30, 50, 75, 100, 125, 150, 200, 250, 300, 500] as const;
export type BudgetPill = (typeof BUDGET_PILLS)[number];

// Domestic-only tiers a sender can pull individual products from when in Add More mode.
// Each entry lists the lower Domestic base collections available for that selected budget.
export const ADD_MORE_TIER_MAP: Record<number, number[]> = {
  30: [],
  50: [30],
  75: [30, 50],
  100: [50, 75],
  125: [75, 100],
  150: [75, 100, 125],
  200: [100, 125, 150],
  250: [125, 150, 200],
  300: [150, 200, 250],
  500: [200, 250, 300],
};

// GfG-global brand assets — served from the PROD bucket's custom domain, read by every env (see the
// hosting-rule note by UB_VIDEO_URL below).
export const GFG_LOGO =
  "https://assets.giftsforgood.com/orgs/giftsforgood/static/branding/gfg-logo.png";
// 1000x500 version of the same stacked lockup, used where the logo renders large (recipient-flow
// header, gift-link-unavailable page). Served from the storefront CDN because the prod R2 brand
// bucket has no high-res copy yet (see the hosting-rule note by UB_VIDEO_URL).
export const GFG_LOGO_HIRES =
  "https://www.giftsforgood.com/cdn/shop/files/Gifts_for_Good_logo1000pixels.png";
// Product-card fallback when an image URL fails to load (GFG-373). Stand-in for the ticket's
// 2000x2000 branded image — swap the value here once that asset is uploaded.
export const PRODUCT_IMAGE_FALLBACK = GFG_LOGO;

export const GFG__MAP_LOGO =
  "https://assets.giftsforgood.com/orgs/giftsforgood/static/branding/Footer_Map_Graphic_Transparent.webp";
// Certification marks shown in the recipient footer (knocked to white via a CSS filter).
export const CERT_BCORP =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/bcorp.svg?v=1783960801";
export const CERT_WOMEN_OWNED =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/women-owned.svg?v=1783960801";
export const FOOTER_IMG =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/Poweredby_GF.png?v=1743624899";
export const UB_RECIPIENTS_IMG =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/Recipients_Graphic.png?v=1777057485";
// Gift envelope graphic shown in the gift-message ("Read Message") modal.
export const GIFT_ENVELOPE_ICON =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/black-envelope-full.png?v=1783846158";
// Fallback landing-page hero banner when the campaign has no `bannerUrl`.
export const DEFAULT_LANDING_BANNER =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/GF-Banner---Default.jpg?v=1775087624";
// GfG-global brand assets live on the PROD bucket's custom domain and are read by every env
// (prod is stable + CDN-cached; the prod bucket has no `pub-*.r2.dev` public URL, only this custom
// domain). Reading prod from staging is the safe dependency direction — never the reverse. Keep
// these keys duplicated into the prod bucket (`orgs/giftsforgood/static/...`).
export const UB_VIDEO_URL =
  "https://assets.giftsforgood.com/orgs/giftsforgood/static/videos/main-unboxing.mp4";
// Fallback landing gift-video when the Landing "Video" toggle is ON but no YouTube/Vimeo link was
// pasted. A hosted R2 mp4 (sibling of UB_VIDEO_URL), played via a native <video> in GiftVideoModal.
export const DEFAULT_LANDING_VIDEO =
  "https://assets.giftsforgood.com/orgs/giftsforgood/static/videos/default-redemption.mp4";

export const LANDING_CONFETTI_Z = 10050;
// Step 3 landing-preview product drawer: above the fullscreen overlay (z-9999), below the confetti.
export const LANDING_PREVIEW_DETAIL_Z = 10001;
// Unbox video volume when played with sound (recipient flow).
export const UB_VIDEO_VOLUME = 0.5;
// Custom-video upload limits (size/duration/formats) live in constants/uploadLimits.ts.

export const TOKENS: TokenEmail[] = [
  { label: "First Name", tag: "@FirstName" },
  { label: "Last Name", tag: "@LastName" },
  { label: "Sender First Name", tag: "@SenderFirstName" },
  { label: "Sender Last Name", tag: "@SenderLastName" },
  { label: "Sender Company", tag: "@SenderCompany" },
  { label: "Sender Signature", tag: "@Signature" },
];

/**
 * The merge tags offerable for a given link type. When the links are ANONYMOUS — manual "No, just
 * set a quantity" (Path A), or a universal link — there are no recipient rows, so @FirstName /
 * @LastName (RECIPIENT_ONLY_TAGS) can never resolve, and they're dropped from BOTH the "Personalize
 * with tags" dropdown and the inline @-mention list. A gfg campaign AND a manual "link to a list"
 * campaign (Path B) both have named recipients, so they keep every tag. Mirrors the prototype's
 * `_availableMergeTags`; sender tokens + @Signature always stay (they resolve from the sender fields).
 */
export function availableTokens(anonymousLinks: boolean): TokenEmail[] {
  if (!anonymousLinks) return TOKENS;
  const blocked = new Set<string>(RECIPIENT_ONLY_TAGS);
  return TOKENS.filter((t) => !blocked.has(t.tag));
}

// 16:9 = the landing hero frame; email + landing share it (GFG-455).
export const HERO_BANNER_UPLOAD_HINT = "Recommended: 1600 × 900px (16:9) — PNG or JPG";

export const BANNER_LIBRARY = [
  {
    name: "Banner 1",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/12GF-Banner.png?v=1775190737",
  },
  {
    name: "Banner 2",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/13GF-Banner.png?v=1775190734",
  },
  {
    name: "Banner 3",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/8GF-Banner.png?v=1775190735",
  },
  {
    name: "Banner 4",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/6GF-Banner.png?v=1775190734",
  },
  {
    name: "Banner 5",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/14GF-Banner.png?v=1775190735",
  },
  {
    name: "Banner 6",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/1GF-Banner.png?v=1775190734",
  },
  {
    name: "Banner 7",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/16GF-Banner.png?v=1775190735",
  },
  {
    name: "Banner 8",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/10GF-Banner.png?v=1775190735",
  },
  {
    name: "Banner 9",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/9GF-Banner.png?v=1775190735",
  },
  {
    name: "Banner 10",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/15GF-Banner.png?v=1775190737",
  },
  {
    name: "Banner 11",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/7GF-Banner.png?v=1775190734",
  },
  {
    name: "Banner 12",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/5GF-Banner.png?v=1775190737",
  },
  {
    name: "Banner 13",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/4GF-Banner.png?v=1775190738",
  },
  {
    name: "Banner 14",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/3GF-Banner.png?v=1775190738",
  },
  {
    name: "Banner 15",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/11GF-Banner.png?v=1775190738",
  },
  {
    name: "Banner 16",
    img: "https://cdn.shopify.com/s/files/1/1826/6559/files/2GF-Banner.png?v=1775190738",
  },
];

// ── Reminder schedule definitions ─────────────────────────────────────────
export const REMINDER_SCHEDULES: Record<number, Array<{ label: string; timing: string }>> = {
  4: [
    { label: "Email 1", timing: "48 hours after main send" },
    { label: "Email 2", timing: "7 days after main send" },
    { label: "Email 3", timing: "7 days before expiration" },
    { label: "Email 4", timing: "1 day before expiration" },
  ],
  3: [
    { label: "Email 1", timing: "48 hours after main send" },
    { label: "Email 2", timing: "7 days after main send" },
    { label: "Email 3", timing: "2 days before expiration" },
  ],
  2: [
    { label: "Email 1", timing: "48 hours after main send" },
    { label: "Email 2", timing: "7 days before expiration" },
  ],
  1: [{ label: "Email 1", timing: "7 days after main send" }],
  0: [],
};

// ── Campaign Builder draft state defaults ──────────────────────────────────
/**
 * sessionStorage key holding the id of the campaign currently open in the builder. Present
 * → EDIT mode (loaded on open, saves PATCH it); absent → CREATE mode (first save on Step 1
 * creates the draft, then its id is stashed here). Cleared on archive. Per-tab (sessionStorage).
 */
export const CAMPAIGN_BUILDER_ID_KEY = "campaignBuilderId";

/**
 * Release the builder's stashed campaign — call this before navigating to /campaign/create.
 *
 * The stash is what lets a mid-build refresh resume where the client left off, so the builder
 * reads it on mount. That makes "+" ambiguous: without releasing it first, a NEW campaign reopens
 * whatever was last built — including a campaign that has since launched, which then takes step
 * autosaves it was never meant to receive.
 *
 * Clearing on the click rather than on mount keeps both behaviours: "+" starts fresh, a refresh
 * still resumes. Resuming a specific campaign deliberately is `loadCampaign(id)`.
 */
export function releaseCampaignBuilderDraft(): void {
  sessionStorage.removeItem(CAMPAIGN_BUILDER_ID_KEY);
}

export const DEFAULT_UB_SETTINGS: UnboxingSettings = {
  tokenFirstName: true,
  tokenLastName: false,
  tokenCompany: true,
  tokenSignature: false,
  showGiftMessage: false,
  giftMessageText: "",
  skipUnboxing: false,
  customVideoUrl: "",
};

// Fallback used when a reminder index has no state yet.
export const DEFAULT_REMINDER_EMAIL: ReminderEmailState = {
  subject: "A friendly reminder from @Signature",
  messageHtml:
    "<p>Hi @FirstName,</p><p><br></p><p>Just a quick reminder — you have a gift waiting from @Signature. Click below to choose your gift.</p>",
  showLogo: true,
  showBanner: false,
};

// Per-reminder defaults matching the prototype (index 0 = Email 1, etc.)
export const REMINDER_DEFAULTS: Record<number, ReminderEmailState> = {
  0: {
    subject: "A friendly reminder from @Signature",
    messageHtml:
      "<p>Hi @FirstName,</p><p><br></p><p>Just a quick reminder — you have a gift waiting from @Signature. Click below to choose your gift.</p>",
    showLogo: true,
    showBanner: false,
  },
  1: {
    subject: "Your gift from @Signature is still waiting",
    messageHtml:
      "<p>Hi @FirstName,</p><p><br></p><p>You still have a gift waiting from @Signature. We'd hate for you to miss out on choosing something you love.</p>",
    showLogo: true,
    showBanner: false,
  },
  2: {
    subject: "Your gift expires soon",
    messageHtml:
      "<p>Hi @FirstName,</p><p><br></p><p>Your gift from @Signature is expiring soon. Don't let it go to waste — claim it today.</p>",
    showLogo: true,
    showBanner: false,
  },
  3: {
    subject: "Last chance: your gift expires tomorrow",
    messageHtml:
      "<p>Hi @FirstName,</p><p><br></p><p>This is your final reminder — your gift from @Signature expires tomorrow. Click below to redeem now.</p>",
    showLogo: true,
    showBanner: false,
  },
};

export const DEFAULT_STAGE3_EMAIL: Stage3EmailState = {
  subject: DEFAULT_EMAIL_SUBJECT,
  messageHtml: DEFAULT_EMAIL_MESSAGE_HTML,
  signatureHtml: DEFAULT_EMAIL_SIGNATURE_HTML,
  ctaColor: "#333333",
  logoEnabled: false,
  logoUrl: "",
  logoOriginalUrl: "",
  logoPadding: 0,
  bannerEnabled: false,
  bannerUrl: "",
  bannerOriginalUrl: "",
};

export const DEFAULT_STAGE3_LANDING: LandingSettings = {
  logoEnabled: false,
  logoUrl: "",
  logoOriginalUrl: "",
  logoPadding: 0,
  bannerEnabled: false,
  bannerUrl: "",
  bannerOriginalUrl: "",
  headline: DEFAULT_LANDING_HEADLINE,
  subtitle: DEFAULT_LANDING_SUBTITLE,
  showVideo: false,
  videoUrl: "",
  logoSynced: true,
  heroTextMode: "dark",
  heroBgColor: LANDING_COLOR_DEFAULTS.heroBg,
  envelopeColor: LANDING_COLOR_DEFAULTS.envelope,
  accentColor: LANDING_COLOR_DEFAULTS.accent,
  confettiEnabled: true,
  confettiShape: "mixed",
  confettiScheme: null,
};

// ── Status-lock copy (Step 1 budget · Step 2 recipients) ───────────────────
/**
 * MIRRORS the API's frozen 409 copy VERBATIM — see `BUDGET_LOCKED` / `RECIPIENTS_LOCKED` in
 * `apps/gfgf-api/src/services/CampaignBuilderService.ts:167-168`. The builder renders these as a
 * courtesy so the sender never has to take the raw 409, which only works while both sides say the
 * same thing: if you edit one of these strings, edit its twin in the service too.
 */
export const BUDGET_LOCKED_NOTICE = "Budget is locked once a campaign is scheduled.";
export const RECIPIENTS_LOCKED_NOTICE = "Recipients are locked once a campaign is scheduled.";

/**
 * Padlock tooltip + screen-reader text on a step that is open but read-only. NEW copy — it has no
 * server twin, and it is punctuated like the two above so the lock vocabulary reads consistently.
 */
export const STEP_LOCKED_LABEL = "Locked once a campaign is scheduled.";

/** GFG-413 / GFG-425 — the cap is shared with the API (`@giftforward/naming`); re-exported for the UI. */
export { CAMPAIGN_NAME_MAX_LENGTH };

export const CAMPAIGN_NAME_TEXT = {
  createTitle: "First, let’s name your gift campaign.",
  renameTitle: "Rename your gift campaign.",
  helper: "Only your team sees this. It’s how you’ll find the campaign on your dashboard.",
  label: "Campaign name",
  next: "Next",
  save: "Save",
  cancel: "Cancel",
  tooLong: `Use ${CAMPAIGN_NAME_MAX_LENGTH} characters or fewer.`,
  renameAction: "Rename campaign",
  // Create mode on multi-group plans, where the modal also asks for the funding wallet.
  createWithWalletTitle: "First, let’s set up your gift campaign.",
  helperWithWallet: "Name it and choose the wallet that pays for it. Only your team sees the name.",
} as const;

export const FUNDING_WALLET_TEXT = {
  placeholder: "— Select a wallet —",
  fundingLabel: "Funding wallet",
  groupLabel: "Group wallet",
  reviewTitle: "Select your funding wallet",
  modalHint: "This wallet's group pays for and manages the campaign. You can change it later.",
  loadError: "We couldn’t load your wallets.",
  retry: "Try again",
  noneAvailable: "You don’t have a wallet you can fund campaigns from. Ask an admin for access.",
  managedBy: (group: string) => `This campaign will be managed under the ${group} group.`,
} as const;
